import { ARRAY_ERROR } from "final-form";

import { validateScheduleHolidayRules } from "../../../utils/holidayRules";

export const buildReloadYardValidationErrors = (values) => {
  const errors = {};
  const holidayError = validateScheduleHolidayRules(values.schedule?.holidays);
  if (holidayError) {
    // react-final-form surfaces an array-level error only via the ARRAY_ERROR
    // key (the redux-form `_error` convention is silently ignored).
    // SimpleFormIterator renders it as a FormHelperText after a failed submit.
    const holidayErrors = [];
    holidayErrors[ARRAY_ERROR] = holidayError;
    errors.schedule = {
      ...errors.schedule,
      holidays: holidayErrors,
    };
  }
  // An enabled driver check-in app needs a grace period: it is the second half
  // of the check-in deadline (shipment scheduledDateTime + grace) driverApi
  // enforces, and a blank one means no appointment window is enforced at all.
  // Only an EXPLICIT true is checked here, the same reading driverApi and the
  // Gate Queue menus apply: the gate is opt-in, so a yard holding null has
  // never turned it on and owes no deadline. Requiring a grace period there
  // would refuse the save over a setting nobody touched — and the driver
  // check-in tab keeps its panel mounted while hidden, so that error would land
  // on an input the manager cannot see. DriverCheckInPanel repeats the same
  // rule as a field validator so the error has a visible input to sit on.
  if (
    values.driverCheckInEnabled === true &&
    (values.driverCheckInGraceMinutes === null ||
      values.driverCheckInGraceMinutes === undefined ||
      values.driverCheckInGraceMinutes === "")
  ) {
    errors.driverCheckInGraceMinutes =
      "Required while driver check-in is enabled";
  }
  // The post-check-in message is the one thing every driver reads after
  // checking in, and on a yard with no stops it is their only instruction —
  // so it is owed while the gate is on. Same explicit-true reading as above.
  if (
    values.driverCheckInEnabled === true &&
    isBlank(values.postCheckInMessage)
  ) {
    errors.postCheckInMessage = "Required while driver check-in is enabled";
  }
  const stopErrors = validateDriverStops(values.driverStops);
  if (stopErrors) errors.driverStops = stopErrors;
  return errors;
};

const isBlank = (value) => !value || !String(value).trim();

/**
 * The post-check-in stops (DriverCheckInPanel). The list is optional — none
 * means the yard has no waiting area — but every row kept needs both halves:
 * the label is what the Gate Queue dropdown offers and what a check-in stores
 * as its dockAssignment, the message is what the driver app shows for it — a
 * stop with either missing is a dropdown entry that sends the driver nowhere,
 * or a message nothing can select. Labels must be unique because the check-in
 * row stores only the label; two stops sharing one would show the driver
 * whichever message driverApi found first.
 *
 * Returns a react-final-form array of per-row errors, or undefined when clean.
 */
export const validateDriverStops = (stops) => {
  const rows = Array.isArray(stops) ? stops : [];
  const seen = new Map();
  let anyError = false;
  const errors = rows.map((stop, index) => {
    const row = {};
    const label = stop?.label;
    // A row left entirely blank (Add clicked, nothing typed) is dropped by the
    // save transform rather than refused here.
    if (isBlank(label) && isBlank(stop?.message)) return row;
    if (isBlank(label)) {
      row.label = "Required";
    } else {
      const key = String(label).trim().toLowerCase();
      if (seen.has(key)) row.label = `Duplicate of stop ${seen.get(key) + 1}`;
      else seen.set(key, index);
    }
    if (isBlank(stop?.message)) row.message = "Required";
    if (row.label || row.message) anyError = true;
    return row;
  });
  return anyError ? errors : undefined;
};
